import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { Sprout, Scissors } from 'lucide-react';
import nurseryMaterialService from '../../services/nurseryMaterial';
import orchardService from '../../services/orchard';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { errorMessage } from '../../utils/apiResponse';
import { useOrgTimezone, formatLocalDateInTz } from '../../utils/timezone';
import { formatQuantity } from '../../utils/nurseryMaterial';
import { MATERIAL_KIND_LABELS } from '../../types/nurseryMaterial';
import type { MotherPlant, MotherYield } from '../../types/nurseryMaterial';
import type { CultivarArea } from '../../types/orchard';

/**
 * /nursery/mothers — what propagation material is taken from: nursery lots
 * marked as mother plants (stool beds, stock plants) and orchard plantings
 * marked as wood sources (scion blocks), with what each gave per year and per
 * plant (propagation plan, Phase 1).
 *
 * Taking material from either never reduces it (Rob, 2026-10-12): cutting 10
 * buds does not make the orchard one tree smaller. A lot is made a mother on
 * its own page; a planting is marked here, by a manager.
 */

const inputCls = 'w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm';

const yieldWhat = (y: MotherYield) => (y.what === 'layers' ? 'Layers harvested' : MATERIAL_KIND_LABELS[y.what]);

function MotherCard({ m, orgTz, canManage, onUnmark }: {
  m: MotherPlant; orgTz: string; canManage: boolean; onUnmark: () => void;
}) {
  const navigate = useNavigate();
  const collect = m.kind === 'lot'
    ? `/nursery/material/new?source_lot=${m.id}`
    : `/nursery/material/new?source_area=${m.id}&cultivar_id=${m.cultivar_id}`;
  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs text-neutral-500">{m.kind === 'lot' ? 'Mother plant' : 'Orchard wood source'}</p>
          <p className="text-sm font-semibold text-neutral-800 break-words">
            {[m.cultivar_name, m.rootstock_name].filter(Boolean).join(' / ')}
          </p>
          <p className="text-xs text-neutral-500 break-words">
            {[m.name, m.where, m.tree_type_name].filter(Boolean).join(' · ')}
          </p>
        </div>
        <div className="text-right">
          <p className="text-sm font-semibold text-neutral-800">{m.plants != null ? `${m.plants.toLocaleString('en-AU')} plants` : 'Count not set'}</p>
          {m.planted_on && <p className="text-xs text-neutral-500">{`Planted ${formatLocalDateInTz(m.planted_on.slice(0, 10), orgTz)}`}</p>}
        </div>
      </div>
      {m.yields.length === 0 ? (
        <p className="text-sm text-neutral-500 mt-3">Nothing taken from it yet.</p>
      ) : (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-neutral-500 text-left">
              <tr><th className="py-1 pr-3 font-medium">Year</th><th className="py-1 pr-3 font-medium">Taken</th>
                <th className="py-1 pr-3 font-medium text-right">Total</th><th className="py-1 font-medium text-right">Per plant</th></tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {m.yields.map((y) => (
                <tr key={`${y.year}-${y.what}-${y.unit}`}>
                  <td className="py-1 pr-3 text-neutral-600">{y.year}</td>
                  <td className="py-1 pr-3 text-neutral-800">{yieldWhat(y)}</td>
                  <td className="py-1 pr-3 text-right text-neutral-800 whitespace-nowrap">{formatQuantity(y.quantity, y.unit)}</td>
                  <td className="py-1 text-right text-neutral-800">{y.per_plant ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="flex flex-wrap gap-2 mt-3">
        <Button variant="primary" size="sm" onClick={() => navigate(collect)}><Scissors className="h-4 w-4" /> Collect material</Button>
        {m.kind === 'lot' && (
          <Button variant="outline" size="sm" onClick={() => navigate(`/nursery/lots/${m.id}`)}>Open the lot to harvest layers</Button>
        )}
        {m.kind === 'cultivar_area' && canManage && (
          <Button variant="outline" size="sm" onClick={onUnmark}>Not a wood source</Button>
        )}
      </div>
    </div>
  );
}

function MotherPlants() {
  const orgTz = useOrgTimezone();
  const { hasAccess: canManage } = useModuleLicense('nursery', ['manager', 'admin']);
  const [mothers, setMothers] = useState<MotherPlant[] | null>(null);
  const [areas, setAreas] = useState<CultivarArea[]>([]);
  const [areaId, setAreaId] = useState<number | ''>('');
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await nurseryMaterialService.mothers();
      if (res.success) setMothers(res.data);
      else setError(errorMessage(res, 'Could not load the mother plants'));
    } catch (e) {
      setError(errorMessage(e, 'Could not load the mother plants'));
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!canManage) return;
    orchardService.getAllCultivarAreas()
      .then((r) => { if (r.success) setAreas(r.data.areas || []); })
      .catch(() => { /* nothing to mark */ });
  }, [canManage]);

  const marked = useMemo(() => new Set((mothers || []).filter((m) => m.kind === 'cultivar_area').map((m) => m.id)), [mothers]);
  const markable = areas.filter((a) => a.cultivar_id != null && !marked.has(a.id));

  const setWood = async (id: number, on: boolean) => {
    setError(null);
    try {
      const res = await nurseryMaterialService.setWoodSource(id, on);
      if (!res.success) { setError(errorMessage(res, 'Could not change the planting')); return; }
      setAreaId('');
      load();
    } catch (e) {
      setError(errorMessage(e, 'Could not change the planting'));
    }
  };

  return (
    <>
      <PageHeader
        title="Mother plants"
        subtitle="Stool beds, stock plants and scion blocks material is taken from"
        icon={<Sprout className="h-6 w-6 text-white" />}
      />
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        <p className="text-sm text-neutral-600">
          Taking material from a mother never reduces it. Mark a nursery lot as a mother plant on its own page.
        </p>
        {error && <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">{error}</div>}

        {canManage && markable.length > 0 && (
          <div className="bg-white border border-neutral-200 rounded-xl p-4">
            <label htmlFor="mark-area" className="block text-sm font-medium text-neutral-700 mb-1">Add an orchard planting as a wood source</label>
            <div className="flex flex-col sm:flex-row gap-2">
              <select id="mark-area" className={inputCls} value={areaId} onChange={(e) => setAreaId(e.target.value ? Number(e.target.value) : '')}>
                <option value="">Choose a planting…</option>
                {markable.map((a) => (
                  <option key={a.id} value={a.id}>
                    {[a.farm_name, a.block_name, a.name, a.cultivar_name].filter(Boolean).join(' · ')}
                  </option>
                ))}
              </select>
              <Button variant="primary" size="sm" disabled={areaId === ''} onClick={() => areaId !== '' && setWood(Number(areaId), true)}>
                Add
              </Button>
            </div>
          </div>
        )}

        {mothers == null ? (
          <div className="h-24 bg-neutral-100 rounded-xl animate-pulse" />
        ) : mothers.length === 0 ? (
          <div className="bg-white border border-dashed border-neutral-300 rounded-xl px-6 py-10 text-center">
            <p className="text-sm text-neutral-600">No mother plants or wood sources yet.</p>
            <p className="text-xs text-neutral-400 mt-1">
              Mark a stool bed or stock plants as a mother on its lot page, or add an orchard planting here.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {mothers.map((m) => (
              <MotherCard key={`${m.kind}-${m.id}`} m={m} orgTz={orgTz} canManage={canManage}
                onUnmark={() => setWood(m.id, false)} />
            ))}
          </div>
        )}
      </div>
    </>
  );
}

export default MotherPlants;
